<template>
  <div>
    <!-- Brand Banner -->
    <div class="brand-banner">
      <picture v-if="bannerImage">
        <source
          v-if="bannerImageMobile"
          media="(max-width: 767px)"
          :srcset="bannerImageMobile"
        />
        <img
          :src="bannerImage"
          :alt="`${brandName} banner`"
          class="brand-banner-image"
        />
      </picture>
      <div v-else class="brand-banner-fallback">
        <h1 class="brand-banner-title">{{ brandName }}</h1>
      </div>
    </div>

    <div class="container">
      <div class="row g-4 mt-0">
        <div class="col-lg-3 d-none d-lg-block">
          <!-- Filters -->
          <div class="filter-section">
            <div class="card">
              <div class="card-body">
                <h2 class="filter-title mb-4">Filter by Price</h2>

                <div class="price-filter">
                  <div class="price-values d-flex justify-content-between mb-2">
                    <span>৳ {{ minPrice }}</span>
                    <span>৳ {{ maxPrice }}</span>
                  </div>
                  <div class="range-slider">
                    <div class="range-track"></div>
                    <div class="range-track-active" :style="activeTrackStyle"></div>
                    <input
                      type="range"
                      :min="PRICE_MIN"
                      :max="PRICE_MAX"
                      step="100"
                      v-model.number="minPrice"
                      @input="onMinInput"
                      class="range-input range-min"
                    />
                    <input
                      type="range"
                      :min="PRICE_MIN"
                      :max="PRICE_MAX"
                      step="100"
                      v-model.number="maxPrice"
                      @input="onMaxInput"
                      class="range-input range-max"
                    />
                  </div>
                  <button
                    class="btn apply-filter-btn mt-4 w-100"
                    @click="applyPriceFilter"
                  >
                    Apply Filter
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-lg-9">
          <div v-if="isLoading" class="text-center m-5">
            <div class="spinner-border mb-2" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p>Loading amazing products...</p>
          </div>
          <!-- Product Section -->
          <div v-else class="product-section">
            <div v-if="productList.length" class="row g-4">
              <!-- Product Card -->
              <div
                v-for="(product, i) in productList"
                :key="`product-${i}`"
                class="col-6 col-md-3"
              >
                <div class="product-card">
                  <router-link
                    :to="'/product/' + product._id + `/${slugify(product.title)}`"
                    style="text-decoration: none"
                  >
                    <img
                      :src="
                        product.images?.length
                          ? `${BASE_IMAGE_PREFIX}/${product.images[0]}`
                          : `${PLACEHOLDER_IMAGE}`
                      "
                      alt="Product image"
                      class="product-image"
                    />
                    <h5 class="product-title">{{ product.title }}</h5>
                  </router-link>
                  <div
                    v-if="product.productCategory !== 'stationary'"
                    class="product-brand"
                  >
                    {{ product.brand }}
                  </div>
                  <div class="product-price" v-if="product.minVariant">
                    <span v-if="product.minVariant.isOnSale && product.minVariant.salePrice">
                      <span class="text-decoration-line-through text-muted me-1">
                        ৳ {{ product.minVariant.price }}
                      </span>
                      <span>৳ {{ product.minVariant.salePrice }}</span>
                    </span>
                    <span v-else>৳ {{ product.minVariant.price }}</span>
                  </div>
                  <!-- Product actions-->
                  <div
                    class="card-footer p-0 pb-4 pt-0 border-top-0 bg-transparent"
                  >
                    <div class="text-center">
                      <span
                        class="btn btn-outline-dark btn-sm mt-auto me-1 text-uppercase"
                        style="font-size: 10px"
                        @click="addToCart(product)"
                      >
                        <i class="bi bi-cart3"></i> Add to cart
                      </span>
                      <span
                        class="btn btn-outline-dark btn-sm mt-auto me-1 text-uppercase"
                        style="font-size: 10px"
                        @click="addToWishlist(product)"
                      >
                        <i class="bi bi-suit-heart"></i>
                      </span>
                      <span
                        class="btn btn-outline-dark btn-sm mt-auto text-uppercase"
                        style="font-size: 10px"
                        @click="addToCompare(product)"
                      >
                        <i class="bi bi-arrow-left-right"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="text-center mt-5">
              <p>No products available.</p>
            </div>
          </div>
          <!-- Pagination -->
          <div
            v-if="totalPages > 1"
            class="d-flex flex-wrap justify-content-center justify-content-md-end align-items-center my-4"
          >
            <nav class="overflow-auto">
              <ul class="pagination mb-0 d-flex align-items-center">
                <li
                  class="page-item me-1"
                  :class="{ disabled: currentPage === 1 }"
                >
                  <button
                    class="page-link page-count"
                    @click="changePage(currentPage - 1)"
                  >
                    <i class="bi bi-arrow-left"></i>
                  </button>
                </li>
                <li
                  class="page-item mx-1"
                  v-for="(page, index) in visiblePages"
                  :key="index"
                >
                  <button
                    v-if="page !== '...'"
                    class="page-link page-count"
                    :class="{ active: page === currentPage }"
                    @click="changePage(page)"
                  >
                    {{ page }}
                  </button>
                  <span v-else class="page-link bg-transparent border-0 disabled fw-bolder">...</span>
                </li>
                <li
                  class="page-item me-1"
                  :class="{ disabled: currentPage === totalPages }"
                >
                  <button
                    class="page-link page-count"
                    @click="changePage(currentPage + 1)"
                  >
                    <i class="bi bi-arrow-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { BASE_URL, BASE_IMAGE_PREFIX, PLACEHOLDER_IMAGE } from "@/globals";
import { analytics } from '@/services/analytics';

export default {
  name: "BrandPage",
  data() {
    return {
      BASE_IMAGE_PREFIX,
      PLACEHOLDER_IMAGE,
      brandQuery: null,
      brandName: "",
      bannerImage: null,
      bannerImageMobile: null,
      isLoading: false,
      productList: [],
      PRICE_MIN: 0,
      PRICE_MAX: 10000,
      minPrice: 0,
      maxPrice: 10000,
      currentPage: 1,
      totalPages: 1,
      totalProducts: 0,
      limit: 20,
    };
  },
  computed: {
    activeTrackStyle() {
      const range = this.PRICE_MAX - this.PRICE_MIN;
      const left = ((this.minPrice - this.PRICE_MIN) / range) * 100;
      const right = ((this.PRICE_MAX - this.maxPrice) / range) * 100;
      return { left: `${left}%`, right: `${right}%` };
    },
    visiblePages() {
      const total = this.totalPages;
      const current = this.currentPage;
      const delta = 1;

      let start = Math.max(1, current - delta);
      let end = Math.min(total, current + delta);
      let pages = [];

      if (start > 1) {
        pages.push(1);
        if (start > 2) pages.push("...");
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (end < total) {
        if (end < total - 1) pages.push("...");
        pages.push(total);
      }

      return pages;
    }
  },
  mounted() {
    this.updateFromRoute(this.$route)
  },
  beforeRouteUpdate(to, from, next) {
    this.updateFromRoute(to)
    next();
  },
  methods: {
    updateFromRoute(route) {
      this.brandQuery = route.params.slug || null
      // Human-friendly fallback name until the brand API responds.
      this.brandName = this.brandQuery
        ? this.brandQuery.replace(/_/g, " ")
        : ""

      const pageFromUrl = parseInt(route.query.page, 10)
      this.currentPage = pageFromUrl > 0 ? pageFromUrl : 1

      this.fetchBrand()
      this.search()
      window.scrollTo(0, 0)
    },
    async fetchBrand() {
      if (!this.brandQuery) return;
      // Reset so a previous brand's banner never lingers on navigation.
      this.bannerImage = null;
      this.bannerImageMobile = null;
      // The banner is keyed by a lowercase, underscored slug (e.g. "april_skin"),
      // i.e. the underscored products `brand` value lowercased.
      const slug = this.brandQuery.toLowerCase();
      if (!slug) return;
      try {
        const response = await axios.get(
          `${BASE_URL}/api/brand-banners/${encodeURIComponent(slug)}`
        );
        const banner = response.data;
        if (banner?.name) {
          this.brandName = banner.name;
        }
        this.bannerImage = this.resolveImage(banner?.bannerUrl);
        this.bannerImageMobile = this.resolveImage(banner?.bannerUrlMobile);
      } catch (error) {
        // 404 (no active banner) or any error: fall back to the CSS banner.
        this.bannerImage = null;
        this.bannerImageMobile = null;
      }
    },
    resolveImage(path) {
      if (!path) return null;
      return /^https?:\/\//.test(path) ? path : `${BASE_IMAGE_PREFIX}/${path}`;
    },
    async search() {
      this.isLoading = true;
      try {
        const response = await axios.get(`${BASE_URL}/api/products`, {
          params: {
            brand: this.brandQuery || undefined,
            minPrice: this.minPrice > this.PRICE_MIN ? this.minPrice : undefined,
            maxPrice: this.maxPrice < this.PRICE_MAX ? this.maxPrice : undefined,
            page: this.currentPage,
            limit: this.limit,
            hasActiveVariant: true,
          },
        });

        this.productList = response.data.products;
        this.totalPages = response.data.totalPages;
        this.totalProducts = response.data.totalProducts;
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        this.isLoading = false;
      }
    },
    addToCart(product) {
      const cartItem = {
        product: product,
        quantity: 1,
        variant: product.minVariant
      };

      this.$store.dispatch("addToCart", cartItem);

      this.$root.showToast({
        message: "Item added to cart",
        status: "success",
        duration: 3000,
      });
      analytics.addToCart(product,
        product.minVariant.salePrice || product.minVariant.price, 1)
    },
    addToWishlist(product) {
      const wishedItem = {
        ...product,
        quantity: 1,
        variant: product.minVariant
      };
      this.$store.dispatch("addToWishlist", wishedItem);
      this.$root.showToast({
        message: "Item added to wishlist",
        status: "success",
        duration: 3000,
      });
      analytics.addToWishlist(product,
        product.minVariant.salePrice || product.minVariant.price, 1)
    },
    addToCompare(product) {
      const compareItems = this.$store.getters.compareItems;
      if (compareItems.includes(product._id)) {
        this.$root.showToast({
          message: "Product is already in compare list",
          status: "error",
          duration: 3000,
        });
        return;
      }
      if (compareItems.length >= 2) {
        this.$root.showToast({
          message: "Maximum 2 items can be compared at a time",
          status: "error",
          duration: 3000,
        });
        return;
      }
      this.$store.dispatch("addToCompare", product._id);
      this.$root.showToast({
        message: "Item added to compare",
        status: "success",
        duration: 3000,
      });
    },
    onMinInput() {
      if (this.minPrice > this.maxPrice) {
        this.minPrice = this.maxPrice;
      }
    },
    onMaxInput() {
      if (this.maxPrice < this.minPrice) {
        this.maxPrice = this.minPrice;
      }
    },
    applyPriceFilter() {
      this.currentPage = 1;
      this.search();
    },
    changePage(page) {
      if (page < 1 || page > this.totalPages) return;
      this.currentPage = page;
      this.$router.replace({
        query: {
          ...this.$route.query,
          page: page
        }
      });
      this.search();
    },
    slugify(text) {
      return text
        .toString()
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, "")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-");
    },
  },
};
</script>

<style lang="scss" scoped>
/* Brand Banner */
.brand-banner {
  width: 100%;
  margin-bottom: 24px;

  .brand-banner-image {
    width: 100%;
    height: 280px;
    object-fit: cover;
    display: block;
  }

  .brand-banner-fallback {
    width: 100%;
    height: 280px;
    background-color: #1a1a1a;
    display: flex;
    align-items: center;
    justify-content: center;

    .brand-banner-title {
      color: #fff;
      font-size: 48px;
      font-weight: 700;
      letter-spacing: 1px;
      text-align: center;
      margin: 0;
      padding: 0 20px;
    }
  }
}

@media (max-width: 767px) {
  .brand-banner {
    .brand-banner-image,
    .brand-banner-fallback {
      height: 160px;
    }

    .brand-banner-fallback .brand-banner-title {
      font-size: 28px;
    }
  }
}

.category-text {
  font-size: 15px;
  font-weight: 400;
  color: #333;
}

.category-text:hover {
  opacity: 0.8;
  color: var(--primary-color);
}

/* Product Grid */
.product-card {
  background: white;
  border-radius: 8px;
  padding: 0 20px 0 0;
  text-align: center;
  transition: transform 0.3s;
  position: relative;
}

.product-card::after {
  content: "";
  position: absolute;
  top: 0;
  right: 0;
  width: 1px;
  height: 100%;
  background-color: #ccc;
  opacity: 0.3;
}

.product-card:hover {
  transform: translateY(-5px);
}

.product-image {
  width: 100%;
  height: auto;
  border-radius: 15px;
  margin-bottom: 15px;
}

.product-title {
  font-weight: 600;
  margin-bottom: 8px;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.product-brand {
  color: var(--text-gray);
  font-size: 14px;
  margin-bottom: 8px;
}

.product-price {
  color: var(--primary-color);
  font-weight: 700;
  font-size: 18px;
}

// Filters
.filter-section {
  .card {
    border-radius: 16px;
    border: 1px solid rgba(0, 0, 0, 0.1);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    margin-bottom: 15px;
  }

  .filter-title {
    font-size: 24px;
    font-weight: 600;
    color: #000;
  }

  .price-filter {
    .price-values {
      font-size: 14px;
      font-weight: 600;
      color: #333;
    }

    .range-slider {
      position: relative;
      height: 24px;

      .range-track,
      .range-track-active {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        height: 4px;
        border-radius: 4px;
      }

      .range-track {
        left: 0;
        right: 0;
        background: #e2e2e2;
      }

      .range-track-active {
        background: var(--secondary-color);
      }

      .range-input {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 24px;
        margin: 0;
        background: none;
        pointer-events: none;
        -webkit-appearance: none;
        appearance: none;

        &::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          pointer-events: auto;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: #fff;
          border: 2px solid var(--secondary-color);
          cursor: pointer;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
        }

        &::-moz-range-thumb {
          pointer-events: auto;
          width: 18px;
          height: 18px;
          border: 2px solid var(--secondary-color);
          border-radius: 50%;
          background: #fff;
          cursor: pointer;
        }
      }
    }

    .apply-filter-btn {
      background-color: var(--secondary-color);
      color: #fff;
      font-weight: 500;
      border: none;

      &:hover {
        opacity: 0.9;
      }
    }
  }
}

.page-count {
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  font-size: 14px;
  font-weight: 500;
  border: none;
  background-color: #f8f9fa;
  color: #333;
  transition: all 0.2s ease;

  &:hover {
    background-color: #e2e6ea;
    cursor: pointer;
  }

  &.active,
  &:focus {
    background-color: var(--primary-color);
    color: white;
  }
}

@media (max-width: 991px) {
  .page-count {
    width: 30px;
    height: 30px;
    font-size: 10px;
  }
}
</style>
